import {Cents, CheckPayment} from "./transaction"
import {createResource, getResource, updateResource} from "./common"

type Account = {
	type: string
	id: string
}

type Customer = {
	type: string
	id: string
}

type Org = {
	type: "org"
	id: string
}

export enum StopPaymentStatus {
	Active = "Active",
	Disabled = "Disabled",
}

export enum StopPaymentType {
	Check = "Check",
	Ach = "Ach",
}

export enum DisableReason {
	Expired = "Expired",
	PaymentStopped = "PaymentStopped",
	Requested = "Requested",
}

export type CheckStopPayment = {
	type: "checkStopPayment"
	id: string
	attributes: {
		createdAt: Date
		updatedAt: Date
		amount?: Cents
		status: StopPaymentStatus
		disableReason?: DisableReason
		tags?: {[key: string]: string}
		checkNumber: string
	}
	relationships: {
		account: {
			data: Account
		}
		customer: {
			data: Customer
		}
		customers?: {
			data: Customer[]
		}
		org?: {
			data: Org
		}
		stoppedPayments?: {
			data: {
				type: string
				id: string
			}[]
		}
		checkPayment?: {
			data: CheckPayment
		}
	}
}

export type AchStopPayment = {
	type: "achStopPayment"
	id: string
	attributes: {
		createdAt: Date
		updatedAt: Date
		status: StopPaymentStatus
		disableReason?: DisableReason
		tags?: {[key: string]: string}
		expiration?: Date
		minAmount?: Cents
		originatorName?: string[]
		description?: string
		isMultiUse?: boolean
		direction?: string
	}
	relationships: {
		account: {
			data: Account
		}
		customer: {
			data: Customer
		}
		customers?: {
			data: Customer[]
		}
		org?: {
			data: Org
		}
	}
}

export type StopPayment = CheckStopPayment | AchStopPayment

export async function findStopPayments(
	accessToken: string,
	offset: number,
	limit: number,
	sort?: string,
	customerId?: string,
	accountId?: string,
	tags?: string,
	since?: string,
	until?: string,
	orgs?: string[],
	fromAmount?: number,
	toAmount?: number,
	noAmount?: "true" | "false",
	checkNumber?: string,
	status?: StopPaymentStatus[],
	banks?: string[],
	fromMinAmount?: number,
	toMinAmount?: number,
	noMinAmount?: "true" | "false",
	originatorName?: string,
	noOriginatorName?: "true" | "false",
	type?: StopPaymentType
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : undefined
	const banksFilter = banks && banks.length > 0 ? banks : undefined
	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			...(customerId && {customerId: customerId}),
			...(accountId && {accountId: accountId}),
			...(tags && {tags: tags}),
			...(since && {since: since}),
			...(until && {until: until}),
			...(orgs && orgFilter),
			...(banks && banksFilter),
			...(fromAmount && {fromAmount: fromAmount}),
			...(toAmount && {toAmount: toAmount}),
			...(noAmount && {noAmount: noAmount}),
			...(checkNumber && {checkNumber: checkNumber}),
			...(status && {status: status}),
			...(fromMinAmount && {fromMinAmount: fromMinAmount}),
			...(toMinAmount && {toMinAmount: toMinAmount}),
			...(noMinAmount && {noMinAmount: noMinAmount}),
			...(originatorName && {originatorName: originatorName}),
			...(noOriginatorName && {noOriginatorName: noOriginatorName}),
			...(type && {type: type}),
		},
		sort: sort,
		include: "customer,account,org,stoppedPayments,checkPayment",
	}

	return await getResource<Array<StopPayment>>("stop-payments", accessToken, query)
}

export async function getStopPayment(accessToken: string, id: string, include?: string) {
	return await getResource<StopPayment>(`stop-payments/${id}`, accessToken, {include})
}

export async function createCheckStopPayment({
	accessToken,
	accountId,
	amount,
	checkNumber,
	targetCheckPaymentId,
	tags,
	idempotencyKey,
}: {
	accessToken: string
	accountId: string
	amount?: number
	checkNumber?: string
	targetCheckPaymentId?: string
	tags?: Record<string, string>
	idempotencyKey?: string
}) {
	const data = {
		type: "checkStopPayment",
		attributes: {
			...(amount !== undefined && {amount: amount}),
			...(checkNumber && {checkNumber: checkNumber}),
			...(tags && {tags: tags}),
			...(idempotencyKey && {idempotencyKey: idempotencyKey}),
		},
		relationships: {
			account: {
				data: {
					type: "account",
					id: accountId,
				},
			},
			...(targetCheckPaymentId && {
				checkPayment: {
					data: {
						type: "checkPayment",
						id: targetCheckPaymentId,
					},
				},
			}),
		},
	}

	return await createResource<CheckStopPayment>("stop-payments", accessToken, data)
}

export async function createAchStopPayment({
	accessToken,
	accountId,
	expiration,
	minAmount,
	originatorName,
	description,
	isMultiUse,
	direction,
	tags,
	idempotencyKey,
}: {
	accessToken: string
	accountId: string
	expiration?: Date
	minAmount?: number
	originatorName?: string[]
	description?: string
	isMultiUse?: boolean
	direction?: string
	tags?: Record<string, string>
	idempotencyKey?: string
}) {
	const data = {
		type: "achStopPayment",
		attributes: {
			...(expiration && {expiration: expiration.toISOString()}),
			...(minAmount !== undefined && {minAmount: minAmount}),
			...(originatorName && {originatorName: originatorName}),
			...(description && {description: description}),
			...(isMultiUse !== undefined && {isMultiUse: isMultiUse}),
			...(direction && {direction: direction}),
			...(tags && {tags: tags}),
			...(idempotencyKey && {idempotencyKey: idempotencyKey}),
		},
		relationships: {
			account: {
				data: {
					type: "account",
					id: accountId,
				},
			},
		},
	}

	return await createResource<AchStopPayment>("stop-payments", accessToken, data)
}

export async function disableStopPayment({id, accessToken}: {id: string; accessToken: string}) {
	const data = {
		type: "stopPaymentDisable",
		attributes: {},
	}
	return await createResource<StopPayment>(`stop-payments/${id}/disable`, accessToken, data)
}

export async function patchStopPayment({
	id,
	accessToken,
	tags,
}: {
	id: string
	accessToken: string
	tags?: Record<string, string>
}) {
	const data = {
		type: "stopPayment",
		attributes: {
			...(tags && {tags: tags}),
		},
	}
	return await updateResource<StopPayment>(`stop-payments/${id}`, accessToken, data)
}
